import React, { useMemo, useState, useEffect } from "react";
import {
  Card,
  CardBody,
  Row,
  Col,
  Input,
  Button,
  FormGroup,
  Spinner,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Modal,
  ModalBody,
  ModalHeader,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { getActiveColorStyle } from "utils/colorUtils";
import { useTheme2FormPage, Theme2FormFieldsStyle, THEME2_ADMIN_INSETS } from "hooks/useTheme2FormPage";
import { useNavigate, useParams } from "react-router-dom";
import Select from "components/Select";
import {
  useCreateCommunicationsTemplate,
  useGetCommunicationsTemplate,
  useUpdateCommunicationsTemplate,
  useDeleteCommunicationsTemplate
} from "api/communications-templates/service";
import classnames from "classnames";
import { CKEditor } from "ckeditor4-react";
import { useRef } from "react";
import SubHeader from "components/Navbars/SubHeader";

const CommunicationTemplatesAdd = ({ activeColor = "blue", shellThemeVariant = "1" }) => {
  const { t } = useTranslation();
  const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
  const {
    isTheme2,
    inputStyle,
    inputClassName,
    selectStyles,
    theme2FieldsCss: baseTheme2FieldsCss,
    theme2BorderColor,
    contentClassName,
    formFieldsClassName,
  } = useTheme2FormPage({
    shellThemeVariant,
    activeColorStyle,
    pageKey: "communication-templates-add",
    insetClass: THEME2_ADMIN_INSETS.communicationTemplates,
    withSelectStyles: true,
  });
  const [isMobile, setIsMobile] = useState(window.innerWidth < 992);

  const theme2FieldsCss = useMemo(
    () =>
      isTheme2
        ? `${baseTheme2FieldsCss}
          @media (max-width: 991.98px) {
            .sidebar.sidebar-shell-theme-2 ~ .main-panel > .content.communication-templates-add-page.communication-templates-add-page--theme2.theme2-admin-content-compact.theme2-communication-templates-inset {
              padding-top: calc(74px + 45px) !important;
            }
          }
          @media (max-width: 575.98px) {
            .sidebar.sidebar-shell-theme-2 ~ .main-panel > .content.communication-templates-add-page.communication-templates-add-page--theme2.theme2-admin-content-compact.theme2-communication-templates-inset {
              padding-top: calc(64px + 45px) !important;
            }
          }`
        : "",
    [isTheme2, baseTheme2FieldsCss]
  );

  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);

  // Tab State
  const [activeTab, setActiveTab] = useState(t("communicationTemplatesAdd.detailTab") || "DETAIL");

  // Delete Modal State
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  // Form State
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    data_source: null,
    layout: "",
  });

  // Data Fields State
  const [showDataFields, setShowDataFields] = useState(false);
  const [fieldSearchTerm, setFieldSearchTerm] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [expandedTables, setExpandedTables] = useState({});
  const [isLightTheme, setIsLightTheme] = useState(false);
  const editorRef = useRef(null);

  // Theme detection
  useEffect(() => {
    const checkTheme = () => {
      setIsLightTheme(document.body.classList.contains("white-content"));
    };
    checkTheme();
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.body, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 992);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Mutations & Queries
  const createMutation = useCreateCommunicationsTemplate();
  const updateMutation = useUpdateCommunicationsTemplate();
  const deleteMutation = useDeleteCommunicationsTemplate();
  const { data: apiData, isLoading: isFetching } = useGetCommunicationsTemplate(id);

  const templateItem = useMemo(() => apiData?.data || apiData, [apiData]);

  const subHeaderTitle = useMemo(() => {
    if (isEditMode) {
      return formData.name || templateItem?.template_id || id;
    }
    return t("communicationTemplatesAdd.addTemplate");
  }, [isEditMode, formData.name, templateItem?.template_id, id, t]);

  const subHeaderStatus = useMemo(() => {
    if (!isEditMode || !templateItem) return "";
    if (templateItem.status) return templateItem.status;
    if (templateItem.is_active === false) return t("common.inactive");
    if (templateItem.is_active === true) return t("common.active");
    return "";
  }, [isEditMode, templateItem, t]);

  const subHeaderStatusColor = useMemo(() => {
    if (!isEditMode || !templateItem) return undefined;
    const status = (templateItem.status || "").toLowerCase();
    if (status === "inactive") return "#dc3545";
    if (status === "active") return "#28a745";
    if (templateItem.is_active === false) return "#dc3545";
    if (templateItem.is_active === true) return "#28a745";
    return undefined;
  }, [isEditMode, templateItem]);

  // Use template data as source for fields
  const fieldsSourceId = id;
  const { data: fieldsData, isLoading: isLoadingFields } = useGetCommunicationsTemplate(fieldsSourceId);
  const tablesSource = fieldsData?.data?.tables || fieldsData?.tables;

  const transformedFields = useMemo(() => {
    if (!tablesSource) return {};
    const tables = {};
    Object.keys(tablesSource).forEach((tableName) => {
      tables[tableName] = tablesSource[tableName].map((fieldObj) => {
        const fieldKey = Object.keys(fieldObj)[0];
        const fieldData = fieldObj[fieldKey];
        return {
          id: fieldKey,
          label: fieldData?.label || fieldKey.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()),
          type: fieldData?.type || "text",
          value: fieldKey,
          tableName: tableName
        };
      });
    });
    return tables;
  }, [tablesSource]);

  const filteredFields = useMemo(() => {
    let filtered = transformedFields;

    // Apply type filter
    if (activeFilter !== "all") {
      const typeMap = {
        text: "text",
        number: ["number", "id"],
        date: "date",
        currency: ["currency", "price"],
        phone: "phone",
        percentage: ["percentage", "percent"],
      };

      const typesToFilter = typeMap[activeFilter];
      if (typesToFilter) {
        const newFiltered = {};
        Object.keys(filtered).forEach((tableName) => {
          newFiltered[tableName] = filtered[tableName].filter((field) =>
            Array.isArray(typesToFilter)
              ? typesToFilter.includes(field.type)
              : field.type === typesToFilter
          );
        });
        filtered = newFiltered;
      }
    }

    // Apply search filter
    if (fieldSearchTerm) {
      const searchFiltered = {};
      Object.keys(filtered).forEach((tableName) => {
        const fieldList = filtered[tableName].filter(
          (field) =>
            field.label.toLowerCase().includes(fieldSearchTerm.toLowerCase()) ||
            field.id.toLowerCase().includes(fieldSearchTerm.toLowerCase())
        );
        if (fieldList.length > 0) searchFiltered[tableName] = fieldList;
      });
      filtered = searchFiltered;
    }

    return filtered;
  }, [transformedFields, fieldSearchTerm, activeFilter]);

  // Handle automatic expansion when searching
  useEffect(() => {
    if (fieldSearchTerm) {
      const newExpanded = {};
      Object.keys(filteredFields).forEach((tableName) => {
        if (filteredFields[tableName].length > 0) {
          newExpanded[tableName] = true;
        }
      });
      setExpandedTables((prev) => ({ ...prev, ...newExpanded }));
    }
  }, [fieldSearchTerm, filteredFields]);

  const getFieldTypeIcon = (type) => {
    const baseColor = isLightTheme ? "#6c757d" : "#ffffff";
    const lightBg = isLightTheme ? "#e9ecef" : "rgba(255, 255, 255, 0.1)";

    const iconMap = {
      text: { icon: "fas fa-font", color: baseColor, bg: lightBg },
      number: { icon: "fas fa-hashtag", color: baseColor, bg: lightBg },
      id: { icon: "fas fa-hashtag", color: baseColor, bg: lightBg },
      date: { icon: "fas fa-calendar", color: baseColor, bg: lightBg },
      currency: { icon: "fas fa-dollar-sign", color: baseColor, bg: lightBg },
      price: { icon: "fas fa-dollar-sign", color: baseColor, bg: lightBg },
      percentage: { icon: "fas fa-percent", color: baseColor, bg: lightBg },
      percent: { icon: "fas fa-percent", color: baseColor, bg: lightBg },
      phone: { icon: "fas fa-phone", color: baseColor, bg: lightBg },
    };

    const config = iconMap[type] || iconMap.text;
    return (
      <div
        className="d-flex align-items-center justify-content-center mr-2"
        style={{
          width: "24px",
          height: "24px",
          backgroundColor: config.bg,
          color: config.color,
          fontSize: "10px",
        }}
      >
        <i className={config.icon}></i>
      </div>
    );
  };

  const toggleTableExpansion = (tableName) => {
    setExpandedTables((prev) => ({
      ...prev,
      [tableName]: !prev[tableName],
    }));
  };

  // Handle inserting field into editor
  const insertField = (field) => {
    if (editorRef.current) {
      const placeholder = `{{${field.id}}}`;
      // Use standard hex and clear styles
      const styledPlaceholder = `<span style="background-color: #f0f2f5; padding: 2px 8px; font-family: 'Courier New', Courier, monospace; font-weight: 600; color: #4a5568; display: inline-block; margin: 0 4px; border: 1px solid #ffffffff;">${placeholder}</span>&nbsp;`;
      editorRef.current.insertHtml(styledPlaceholder);
    }
  };

  // Bridge for CKEditor button
  useEffect(() => {
    window.toggleCommunicationDataFields = () => setShowDataFields(prev => !prev);
    return () => { delete window.toggleCommunicationDataFields; };
  }, []);

  // Load data in edit mode
  useEffect(() => {
    if (isEditMode && apiData) {
      const item = apiData?.data || apiData;
      setFormData({
        name: item.name || "",
        description: item.description || "",
        data_source: item.data_source ? {
          label: item.data_source.charAt(0).toUpperCase() + item.data_source.slice(1),
          value: item.data_source
        } : null,
        layout: item.layout || "",
      });
    }
  }, [isEditMode, apiData]);

  const handleSave = () => {
    const submitData = {
      name: formData.name,
      description: formData.description,
      data_source: formData.data_source?.value,
      layout: formData.layout,
    };

    if (isEditMode) {
      updateMutation.mutate({ id, data: submitData });
    } else {
      createMutation.mutate(submitData);
    }
  };

  const handleDelete = () => {
    deleteMutation.mutate(id);
    setDeleteModalOpen(false);
  };

  // Listen for delete request from floating footer
  useEffect(() => {
    const handleDeleteRequest = () => {
      if (isEditMode) {
        setDeleteModalOpen(true);
      }
    };
    window.addEventListener('delete-template-requested', handleDeleteRequest);
    return () => window.removeEventListener('delete-template-requested', handleDeleteRequest);
  }, [isEditMode]);

  // Tab scroll helpers
  const tabScrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScrollPosition = React.useCallback(() => {
    if (tabScrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = tabScrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  }, []);

  useEffect(() => {
    checkScrollPosition();
    const scrollElement = tabScrollRef.current;
    if (scrollElement) {
      scrollElement.addEventListener("scroll", checkScrollPosition);
      window.addEventListener("resize", checkScrollPosition);
      return () => {
        scrollElement.removeEventListener("scroll", checkScrollPosition);
        window.removeEventListener("resize", checkScrollPosition);
      };
    }
  }, [checkScrollPosition, isEditMode]);

  const handleScrollLeft = () => {
    if (tabScrollRef.current && canScrollLeft) {
      tabScrollRef.current.scrollBy({ left: -200, behavior: "smooth" });
    }
  };

  const handleScrollRight = () => {
    if (tabScrollRef.current && canScrollRight) {
      tabScrollRef.current.scrollBy({ left: 200, behavior: "smooth" });
    }
  };

  if (isEditMode && (isFetching || !apiData)) {
    return (
      <div className={classnames(contentClassName, "text-center p-5")}>
        <Theme2FormFieldsStyle css={theme2FieldsCss} />
        <Spinner color="primary" />
        <div className="mt-2">{t("communicationTemplatesAdd.loading")}</div>
      </div>
    );
  }

  const isSaving = createMutation.isLoading || updateMutation.isLoading;
  const canSubmit = Boolean(formData.name && formData.data_source);

  return (
    <div
      className={contentClassName}
      style={{
        minHeight: "80vh",
        paddingTop: isMobile && !isTheme2 ? "7rem" : undefined,
      }}
    >
      <Theme2FormFieldsStyle css={theme2FieldsCss} />
      <div style={{ width: "100%" }}>
        <SubHeader
          activeColor={activeColor}
          back={-1}
          backFallback="/communication-templates"
          title={subHeaderTitle}
          status={subHeaderStatus}
          statusColor={subHeaderStatusColor}
          theme2ScrollGapFill={isTheme2}
          isLightTheme={isLightTheme}
        />
      <Row>
        <Col md="12">
          <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between communication-template-detail-tabs w-100">
            <div
              className="flex-grow-1 mr-3 channel-tabs-scroll"
              ref={tabScrollRef}
              style={{ overflowX: 'auto', scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              <Nav
                tabs
                className="border-0"
                style={{
                  borderBottom: "none",
                  flexWrap: "nowrap",
                  minWidth: "max-content",
                }}
              >
                {["DETAIL", "TEMPLATE LAYOUT", "NOTES"]
                  .filter((tab) => isEditMode || tab === "DETAIL")
                  .map((tab) => (
                    <NavItem key={tab} style={{ flex: "0 0 auto" }}>
                      <NavLink
                        href="#"
                        className={classnames(
                          activeTab === tab
                            ? "active font-weight-bold"
                            : "text-muted"
                        )}
                        style={{
                          background: "none",
                          border: "none",
                          position: "relative",
                          cursor: "pointer",
                          color:
                            activeTab === tab
                              ? activeColorStyle.borderColor
                              : undefined,
                          fontWeight: activeTab === tab ? 700 : 500,
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          setActiveTab(tab);
                        }}
                      >
                        {t(
                          `communicationTemplatesAdd.${tab === "TEMPLATE LAYOUT"
                            ? "templateLayoutTab"
                            : tab === "DETAIL"
                              ? "detailTab"
                              : "notesTab"
                          }`
                        )}
                        {activeTab === tab && (
                          <div
                            style={{
                              height: 3,
                              background: activeColorStyle.borderColor,
                              position: "absolute",
                              left: 0,
                              right: 0,
                              bottom: -8,
                            }}
                          />
                        )}
                      </NavLink>
                    </NavItem>
                  ))}
              </Nav>
            </div>

            <div className="d-flex align-items-center flex-shrink-0">
              <Button
                size="sm"
                color="link"
                onClick={handleScrollLeft}
                disabled={!canScrollLeft}
                style={{ opacity: canScrollLeft ? 1 : 0.3 }}
              >
                <i className="tim-icons icon-minimal-left" />
              </Button>
              <Button
                size="sm"
                color="link"
                onClick={handleScrollRight}
                disabled={!canScrollRight}
                style={{ opacity: canScrollRight ? 1 : 0.3 }}
              >
                <i className="tim-icons icon-minimal-right" />
              </Button>
            </div>

            <div className="d-none d-lg-flex align-items-center flex-shrink-0 ml-2">
              <Button
                color="secondary"
                outline
                size="sm"
                onClick={() => navigate("/communication-templates")}
                disabled={isSaving}
                className="mr-2"
              >
                {t("communicationTemplatesAdd.cancel")}
              </Button>
              <button
                className="btn btn-sm"
                style={{ ...activeColorStyle, color: "white" }}
                onClick={handleSave}
                disabled={isSaving || !canSubmit}
                type="button"
              >
                {isSaving ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm mr-2"
                      role="status"
                      aria-hidden="true"
                    />
                    {t("communicationTemplatesAdd.saving")}
                  </>
                ) : (
                  <>
                    {isEditMode
                      ? t("communicationTemplatesAdd.updateTemplate")
                      : t("communicationTemplatesAdd.saveTemplate")}
                  </>
                )}
              </button>
            </div>

            <div className="d-flex d-lg-none flex-column w-100 mt-2 mb-3">
              <Button
                color="secondary"
                outline
                onClick={() => navigate("/communication-templates")}
                disabled={isSaving}
                className="mb-2 w-100"
                style={{ padding: "12px" }}
              >
                {t("communicationTemplatesAdd.cancel")}
              </Button>
              <button
                className="btn btn-sm w-100"
                style={{ ...activeColorStyle, color: "white", padding: "12px" }}
                onClick={handleSave}
                disabled={isSaving || !canSubmit}
                type="button"
              >
                {isSaving ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm mr-2"
                      role="status"
                      aria-hidden="true"
                    />
                    {t("communicationTemplatesAdd.saving")}
                  </>
                ) : (
                  <>
                    {isEditMode
                      ? t("communicationTemplatesAdd.updateTemplate")
                      : t("communicationTemplatesAdd.saveTemplate")}
                  </>
                )}
              </button>
            </div>
          </div>

          <div className={formFieldsClassName}>
          <TabContent activeTab={activeTab}>
            <TabPane tabId="DETAIL">
              <Card className="shadow-sm border-0">
                <CardBody className="p-4 pt-0">
                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <label className="text-uppercase font-weight-bold" style={{ fontSize: '0.7rem', color: '#888' }}>{t("communicationTemplatesAdd.templateName")}</label>
                        <Input
                          type="text"
                          className={inputClassName()}
                          style={inputStyle}
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row className="mt-2 text-left">
                    <Col md="6">
                      <FormGroup>
                        <label className="text-uppercase font-weight-bold" style={{ fontSize: '0.7rem', color: '#888' }}>{t("communicationTemplatesAdd.templateDescription")}</label>
                        <Input
                          type="text"
                          className={inputClassName()}
                          style={inputStyle}
                          value={formData.description}
                          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <label className="text-uppercase font-weight-bold" style={{ fontSize: '0.7rem', color: '#888' }}>{t("communicationTemplatesAdd.dataSource")}</label>
                        <Select
                          activeColor={activeColor}
                          styles={selectStyles}
                          className="react-select"
                          placeholder={t("communicationTemplatesAdd.selectPrimaryDataSource")}
                          value={formData.data_source}
                          onChange={(option) => setFormData({ ...formData, data_source: option })}
                          isDisabled={isEditMode}
                          options={[
                            { label: t("communicationTemplatesAdd.account"), value: "accounts" },
                            { label: t("communicationTemplatesAdd.inventory"), value: "inventory" },
                            { label: t("communicationTemplatesAdd.deal"), value: "deal" },
                            { label: t("communicationTemplatesAdd.channel"), value: "channel" },
                          ]}
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                </CardBody>
              </Card>
            </TabPane>

            <TabPane tabId="TEMPLATE LAYOUT">
              <Card className="shadow-sm border-0">
                <CardBody className="p-3">
                  {/* Data Fields Selection Section */}
                  {showDataFields && (
                    <div className="data-fields-selector mb-4 p-3 shadow-sm" style={{ background: isLightTheme ? '#ffffff' : '#27293d', border: `1px solid ${isLightTheme ? '#e9ecef' : '#3c3f58'}` }}>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="mb-0 text-uppercase" style={{ fontSize: '0.85rem', color: isLightTheme ? '#2d3748' : '#ffffff', fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                          <i className="fas fa-database mr-2" style={{ color: activeColorStyle.borderColor }} />
                          Data Sources
                        </h6>
                        <Button close onClick={() => setShowDataFields(false)} style={{ color: isLightTheme ? "#000" : "#fff" }} />
                      </div>

                      <div className="d-flex flex-wrap align-items-center justify-content-between mb-3" style={{ gap: '10px' }}>
                        <div className="filter-buttons d-flex" style={{ gap: '5px' }}>
                          {[
                            { key: 'all', icon: 'fas fa-th-large' },
                            { key: 'text', icon: 'fas fa-font' },
                            { key: 'number', icon: 'fas fa-hashtag' },
                            { key: 'date', icon: 'fas fa-calendar' },
                            { key: 'currency', icon: 'fas fa-dollar-sign' },
                            { key: 'phone', icon: 'fas fa-phone' },
                            { key: 'percentage', icon: 'fas fa-percent' },
                          ].map(filter => (
                            <button
                              key={filter.key}
                              className={`btn btn-sm p-0 d-flex align-items-center justify-content-center ${activeFilter === filter.key ? 'active' : ''}`}
                              onClick={() => setActiveFilter(filter.key)}
                              style={{
                                width: '32px',
                                height: '32px',
                                background: activeFilter === filter.key ? activeColorStyle.borderColor : (isLightTheme ? '#f8f9fa' : '#3c3f58'),
                                color: activeFilter === filter.key ? '#fff' : (isLightTheme ? '#6c757d' : '#adb5bd'),
                                border: `1px solid ${activeFilter === filter.key ? activeColorStyle.borderColor : (isLightTheme ? '#dee2e6' : '#45486a')}`,
                                transition: 'all 0.2s'
                              }}
                            >
                              <i className={filter.icon} style={{ fontSize: '11px' }}></i>
                            </button>
                          ))}
                        </div>

                        <div className="position-relative flex-grow-1" style={{ maxWidth: '300px' }}>
                          <Input
                            placeholder="Search fields..."
                            size="sm"
                            className={inputClassName()}
                            value={fieldSearchTerm}
                            onChange={(e) => setFieldSearchTerm(e.target.value)}
                            style={{
                              ...inputStyle,
                              paddingRight: "30px",
                              ...(!isTheme2
                                ? {
                                    border: `1px solid ${isLightTheme ? "#dee2e6" : "#45486a"}`,
                                    background: isLightTheme ? "#fff" : "#1e1e2f",
                                    color: isLightTheme ? "#2d3748" : "#fff",
                                  }
                                : {}),
                            }}
                          />
                          <i className="fas fa-search position-absolute" style={{ right: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '12px', color: '#888' }} />
                        </div>
                      </div>

                      <div className="fields-container px-0" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        {isLoadingFields ? (
                          <div className="text-center py-4"><Spinner size="sm" color="info" /></div>
                        ) : Object.keys(filteredFields).length > 0 ? (
                          Object.keys(filteredFields).map(tableName => (
                            <div key={tableName} className="mb-1 border-bottom-0">
                              <div
                                className="d-flex align-items-center justify-content-between p-2 cursor-pointer"
                                onClick={() => toggleTableExpansion(tableName)}
                                style={{
                                  background: isLightTheme ? '#f4f7f6' : '#2b354e',
                                  transition: 'background 0.2s',
                                  border: `1px solid ${isLightTheme ? '#e9ecef' : '#3c3f58'}`
                                }}
                              >
                                <div className="d-flex align-items-center">
                                  <i className="fas fa-table mr-2" style={{ color: activeColorStyle.borderColor, fontSize: '12px' }} />
                                  <span className="text-capitalize font-weight-bold" style={{ fontSize: '0.85rem', color: isLightTheme ? '#2d3748' : '#ffffff' }}>
                                    {tableName.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                                    <small className="ml-2 font-weight-normal" style={{ opacity: 0.6 }}>
                                      {filteredFields[tableName].length} Fields
                                    </small>
                                  </span>
                                </div>
                                <i className={`fas fa-chevron-${expandedTables[tableName] ? 'up' : 'down'}`} style={{ fontSize: '10px', opacity: 0.5 }} />
                              </div>

                              {expandedTables[tableName] && (
                                <div className="mt-1">
                                  {filteredFields[tableName].map(field => (
                                    <div
                                      key={field.id}
                                      onClick={() => insertField(field)}
                                      className="d-flex align-items-center p-2 field-list-item"
                                      style={{
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        borderBottom: `1px solid ${isLightTheme ? '#f1f3f5' : '#3c3f58'}`,
                                        marginLeft: '5px'
                                      }}
                                    >
                                      {getFieldTypeIcon(field.type)}
                                      <span style={{ fontSize: '0.8rem', color: isLightTheme ? '#495057' : '#ced4da' }}>
                                        {field.label}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))
                        ) : (
                          <div className="text-center py-5 text-muted" style={{ fontSize: '0.9rem' }}>
                            <i className="fas fa-search-minus d-block mb-3" style={{ fontSize: '2rem', opacity: 0.3 }} />
                            {formData.data_source ? "No fields found matching your criteria." : "Please select a Primary Data Source to see fields."}
                          </div>
                        )}
                      </div>
                      <style>{`
                        .field-list-item:hover {
                          background: ${isLightTheme ? '#f8f9fa' : '#3c3f58'} !important;
                          padding-left: 12px !important;
                        }
                        .fields-container::-webkit-scrollbar {
                          width: 4px;
                        }
                        .fields-container::-webkit-scrollbar-track {
                          background: transparent;
                        }
                        .fields-container::-webkit-scrollbar-thumb {
                          background: #888;
                        }
                      `}</style>
                    </div>
                  )}

                  <div
                    className="ckeditor-container"
                    style={{
                      border: `1px solid ${isTheme2 ? theme2BorderColor : "#e9ecef"}`,
                      minHeight: "550px",
                    }}
                  >
                    <style>{`
                      .ckeditor-container {
                        overflow: hidden;
                        border: 1px solid #e3e3e3 !important;
                        box-shadow: 0 10px 30px -12px rgba(0,0,0,0.1);
                        transition: all 0.3s ease;
                        background: #fff;
                      }
                      .ckeditor-container:focus-within {
                        border-color: ${activeColorStyle.borderColor} !important;
                        box-shadow: 0 15px 35px -12px rgba(0,0,0,0.15);
                      }
                      .cke_chrome {
                        border: none !important;
                        box-shadow: none !important;
                        padding: 0 !important;
                      }
                      .cke_inner {
                        background: #fff !important;
                      }
                      .cke_top { 
                        background: #ffffff !important; 
                        padding: 15px !important;
                        border-bottom: 1px solid #f0f0f0 !important;
                        box-shadow: 0 2px 4px rgba(0,0,0,0.02) !important;
                      }
                      .cke_bottom {
                        background: #fafafa !important;
                        padding: 8px 15px !important;
                        border-top: 1px solid #f0f0f0 !important;
                      }
                      .cke_toolgroup {
                        background: #f8f9fa !important;
                        border: 1px solid #eee !important;
                        box-shadow: none !important;
                        margin: 0 8px 5px 0 !important;
                        padding: 2px !important;
                      }
                      .cke_toolbar {
                        margin-bottom: 0 !important;
                      }
                      .cke_button {
                        padding: 4px 6px !important;
                        transition: all 0.2s ease;
                      }
                      .cke_button:hover {
                        background: #fff !important;
                        box-shadow: 0 2px 5px rgba(0,0,0,0.1) !important;
                      }
                      .cke_button_on {
                        background: #fff !important;
                        box-shadow: inset 0 1px 3px rgba(0,0,0,0.1) !important;
                        border: 1px solid #eee !important;
                      }
                      .cke_combo_button {
                        background: #f8f9fa !important;
                        border: 1px solid #eee !important;
                        box-shadow: none !important;
                        padding: 4px 10px !important;
                        margin: 0 8px 5px 0 !important;
                      }
                      .cke_combo_button:hover {
                        background: #fff !important;
                        box-shadow: 0 2px 5px rgba(0,0,0,0.1) !important;
                      }
                      .cke_toolbar_separator {
                        display: none !important;
                      }
                      .cke_status {
                        color: #adb5bd !important;
                        font-family: inherit !important;
                        font-size: 11px !important;
                      }
                    `}</style>
                    <CKEditor
                      key={isEditMode ? (isFetching || !apiData ? "loading" : "loaded") : "new"}
                      editorUrl="https://cdn.ckeditor.com/4.16.2/full/ckeditor.js"
                      initData={isEditMode ? ((apiData?.data || apiData)?.layout || "") : (formData.layout || "")}
                      onNamespaceLoaded={(CKEDITOR) => {
                        if (!CKEDITOR.plugins.get('datafields')) {
                          CKEDITOR.plugins.add('datafields', {
                            init: function (editor) {
                              editor.addCommand('insertTest', {
                                exec: function (editor) {
                                  if (window.toggleCommunicationDataFields) {
                                    window.toggleCommunicationDataFields();
                                  }
                                }
                              });
                              editor.ui.addButton('DataFields', {
                                label: 'Data Fields',
                                command: 'insertTest',
                                toolbar: 'insert',
                                icon: 'https://cdn-icons-png.flaticon.com/512/2991/2991108.png'
                              });
                            }
                          });
                        }
                      }}
                      config={{
                        extraPlugins: 'datafields',
                        toolbar: [
                          { name: 'basicstyles', items: ['Bold', 'Italic', 'Underline', 'Strike', '-', 'RemoveFormat'] },
                          { name: 'paragraph', items: ['NumberedList', 'BulletedList', '-', 'Outdent', 'Indent', '-', 'Blockquote', '-', 'JustifyLeft', 'JustifyCenter', 'JustifyRight', 'JustifyBlock'] },
                          { name: 'links', items: ['Link', 'Unlink'] },
                          { name: 'insert', items: ['Image', 'Table', 'HorizontalRule', 'SpecialChar'] },
                          '/',
                          { name: 'styles', items: ['Format', 'Font', 'FontSize'] },
                          { name: 'colors', items: ['TextColor', 'BGColor'] },
                          { name: 'tools', items: ['Maximize', 'Source', 'Preview'] },
                          { name: 'data', items: ['DataFields'] },
                        ],
                        height: 500,
                        removePlugins: 'exportpdf,elementspath',
                        resize_enabled: false,
                        skin: 'moono-lisa',
                        versionCheck: false,
                        uiColor: '#ffffff',
                        language: 'en',
                        allowedContent: true,
                        extraAllowedContent: 'span[*]{*}',
                      }}
                      onInstanceReady={({ editor }) => {
                        editorRef.current = editor;
                      }}
                      onChange={(evt) => {
                        const newContent = evt.editor.getData();
                        setFormData(prev => ({ ...prev, layout: newContent }));
                      }}
                    />
                    {!formData.layout && !isFetching && (
                      <div className="p-3 text-center text-muted border-top" style={{ fontSize: '0.8rem' }}>
                        Start typing to design your template layout...
                      </div>
                    )}
                  </div>
                </CardBody>
              </Card>
            </TabPane>

            <TabPane tabId="NOTES">
              <Card className="shadow-sm border-0">
                <CardBody>
                  <div className="p-5 text-center">
                    <h5 className="text-muted">{t("communicationTemplatesAdd.noNotes")}</h5>
                  </div>
                </CardBody>
              </Card>
            </TabPane>
          </TabContent>
          </div>
        </Col>
      </Row>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteModalOpen} toggle={() => setDeleteModalOpen(false)} centered={false} style={{ marginTop: '100px' }}>
        <ModalHeader className="justify-content-center">
          <h6 className="title title-up">{t("communicationTemplatesAdd.confirmDeletion")}</h6>
        </ModalHeader>
        <ModalBody className="text-center">
          <p>{t("communicationTemplatesAdd.confirmDeleteText")}</p>
          <div className="mt-4">
            <Button color="secondary" outline onClick={() => setDeleteModalOpen(false)}>{t("communicationTemplatesAdd.cancel")}</Button>
            <Button color="danger" className="ml-3 px-4" onClick={handleDelete} style={{ background: '#e91e63', borderColor: '#e91e63' }}>{t("communicationTemplatesAdd.yesDelete")}</Button>
          </div>
        </ModalBody>
      </Modal>
      </div>
    </div>
  );
};

export default CommunicationTemplatesAdd;
